import React from 'react'
import { Chip, CircularProgress } from '@the-platform-group/component-library'
import { DataGridActionCell } from '@the-platform-group/component-library/DataGrid'
import {
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
} from '@the-platform-group/component-library/Icons'
import { ACTIVE_FIELD, SCOPE_FIELD } from '../constants'
import USER_SCOPES from 'constants/userScopes'
import { useConfirmationDialog } from 'components/ConfirmationDialog'
import useCustomerAuthorizationMutation from './useCustomerAuthorizationMutation'
import useSyncUser from 'pages/Users/hooks/useSyncUser'
import { FDS_STATUS_COLORS } from 'constants/fdsAdmin'

const useColumnConfig = ({ handleUserEdit, customerId }) => {
  const { createConfirmationDialog } = useConfirmationDialog()
  const { removeCustomerAuthorization, restoreCustomerAuthorization } =
    useCustomerAuthorizationMutation({ customerId })
  const { syncUserForRow, isSyncing, syncingRowId } = useSyncUser()
  return [
    {
      field: 'name',
      valueGetter: ({ row: { user, name } }) => ({ to: '/users/' + user.id, text: name }),
      type: 'link',
    },
    {
      field: 'email',
      filterable: true,
      valueGetter: ({ row: { user } }) => user.email,
    },
    {
      field: 'fdsSerial',
      headerName: 'FactSet Username Serial Numbers',
      renderCell: ({ row }) => {
        const details = row.user?.userFdsadminDetails
        if (!details?.length) {
          return null
        }
        return details.map(d => d.usernameSerial).join(', ')
      },
    },
    {
      field: 'fdsStatus',
      headerName: 'FDS Status',
      renderCell: ({ row }) => {
        const details = row.user?.userFdsadminDetails
        if (!details?.length) {
          return null
        }
        return details.map(d => (
          <Chip
            key={d.usernameSerial}
            label={d.status}
            size="small"
            color={FDS_STATUS_COLORS[d.status] || FDS_STATUS_COLORS.default}
            sx={{ mr: 0.5 }}
          />
        ))
      },
    },
    {
      field: SCOPE_FIELD,
      filterable: true,
      type: 'singleSelect',
      valueOptions: Object.entries(USER_SCOPES).map(([value, label]) => ({
        value,
        label,
      })),
    },
    {
      field: ACTIVE_FIELD,
      filterable: true,
      type: 'boolean',
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) => {
        const { id, name, isActive, user } = row

        if (!isActive) {
          return [
            <DataGridActionCell
              icon={<RestoreIcon />}
              title="Restore user to the customer"
              onClick={() => {
                createConfirmationDialog({
                  title: `Restore ${name}?`,
                  body: `Are you sure you want to restore ${name} to this customer?`,
                  onConfirm: () => restoreCustomerAuthorization.mutate(id),
                })
              }}
            />,
          ]
        } else {
          const usernameSerials = user.userFdsadminDetails?.map(detail => detail.usernameSerial)
          return [
            <DataGridActionCell
              icon={
                isSyncing && syncingRowId === row.id ? <CircularProgress size={20} /> : <SyncIcon />
              }
              title="Sync user"
              disabled={(isSyncing && syncingRowId === row.id) || !usernameSerials?.length}
              onClick={() => syncUserForRow(row.id, usernameSerials)}
            />,
            <DataGridActionCell
              icon={<EditIcon />}
              title="Edit User"
              onClick={() => {
                handleUserEdit(row.user)
              }}
            />,
            <DataGridActionCell
              icon={<DeleteIcon />}
              title="Remove user from this customer"
              onClick={() => {
                createConfirmationDialog({
                  title: `Remove ${name}?`,
                  body: `Are you sure you want to deactivate the user ${name}? The deactivated user will no longer be able to access this customer account using the Irwin application.`,
                  onConfirm: () => removeCustomerAuthorization.mutate(id),
                })
              }}
            />,
          ]
        }
      },
    },
  ]
}

export default useColumnConfig
